import { useTranslation } from 'react-i18next';
import { Button } from '../../../web/src/components/interactive/button.jsx';
import { BalanceSparkline } from '../../../web/src/components/display/balance-sparkline.jsx';
import { Amount } from '../../../web/src/components/display/amount.jsx';
import { MockAppFrame } from '../use-cases/mock-app-frame.jsx';
import { useSiteLang } from '../layout/use-site-lang.js';
import { APP_URL } from '../app-config.js';
import { getVisualiseMockData } from '../use-cases/mock-data/visualise-mock-data.js';

const RISING = [
	{ left: '8%', delay: '0s', label: '\u00A5' },
	{ left: '22%', delay: '1.4s', label: '$' },
	{ left: '38%', delay: '0.6s', label: '\u2708' },
	{ left: '62%', delay: '1.9s', label: '\u00A5' },
	{ left: '78%', delay: '0.9s', label: '$' },
	{ left: '90%', delay: '2.4s', label: '\u00A5' },
];

/**
 * Home hero: headline copy and the investment account sparkline card rendered
 * against the dark green background, showing the dual-band balance chart with
 * profit (green) and loss (red) bands.
 * @returns {JSX.Element}
 */
export function HomeHero() {
	const { t } = useTranslation();
	const siteLang = useSiteLang();

	const mock = getVisualiseMockData(siteLang);
	const gain = mock.balance - mock.principal;
	const pct = ((gain / mock.principal) * 100).toFixed(1);

	return (
		<section className="hero">
			<div className="rising-leaves" aria-hidden="true">
				{RISING.map((r, i) => (
					<span key={i} className="rising-leaf" style={{ left: r.left, animationDelay: r.delay }}>{r.label}</span>
				))}
			</div>
			<div className="hero-copy">
				<p className="hero-eyebrow">{t('site.home.eyebrow')}</p>
				<h1 className="hero-title">{t('site.home.title')}</h1>
				<p className="hero-subtitle">{t('site.home.subtitle')}</p>
				<div className="hero-actions">
					<Button as="a" href={APP_URL} target="_blank" variant="primary">{t('site.home.ctaPrimary')}</Button>
				</div>
			</div>
			<div className="hero-visual">
				<div className="hero-cards" data-theme="dark" aria-hidden="true">
					<div className="hero-card">
						<MockAppFrame url={mock.url}>
							<div className="card account-summary">
								<div className="card-header">
									<h2>{t('usecase.visualise.accountName')}</h2>
								</div>
								<div className="form-body">
									<BalanceSparkline
										account={mock.account}
										history={mock.history}
										valuations={mock.valuations}
									/>
									<div className="account-summary-type">
										{t('option.Investment Account')}
									</div>
									<div className="account-summary-balance">
										<Amount value={mock.balance} decimalPlaces={mock.decimalPlaces} symbol={mock.symbol} />
									</div>
									<div className={`account-summary-gain ${gain >= 0 ? 'positive' : 'negative'}`}>
										{gain >= 0 ? '+' : ''}
										<Amount value={Math.abs(gain)} decimalPlaces={mock.decimalPlaces} symbol={mock.symbol} />
										{' ('}{gain >= 0 ? '+' : ''}{pct}%{')'}
									</div>
								</div>
							</div>
						</MockAppFrame>
					</div>
				</div>
			</div>
		</section>
	);
}
